import { NgStyle } from '@angular/common';
import { Component, Inject } from '@angular/core';
import { FormControl, FormsModule, Validators } from '@angular/forms';
import { PlanCheckPipe } from '@app/pipes/plan-check.pipe';
import { TrackingService } from '@app/services/tracking.service';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ButtonThemePalette, DIALOG_DATA, DialogActivationComponent, DialogRef, InputModule } from '@sb/ui';
import { finalize, from, switchMap } from 'rxjs';

import { OnboardingDialogResult } from '../../../+authenticated/shared/onboarding/onboarding-dialog/onboarding-dialog.model';
import { KnowledgeBaseArticleLinkModule } from '../../../pipes/knowledge-base-article-link.module';
import { OnboardingService } from '../../onboarding.service';
import { ChapterRadioButtonData } from '../chapter.model';
import { OnboardingChapterModule } from '../components/chapter.module';
import { AbsenceManagementOnboardingService } from '../services/absence-management-onboarding.service';
import {
  AbsenceManagementDialogData,
  AbsenceManagementDialogStep,
  DefaultScheduledShiftAction,
} from './absence-management.model';

@Component({
  selector: 'absence-management-dialog',
  standalone: true,
  imports: [
    DialogActivationComponent,
    FormsModule,
    NgStyle,
    TranslateModule,
    OnboardingChapterModule,
    InputModule,
    KnowledgeBaseArticleLinkModule,
  ],
  providers: [PlanCheckPipe],
  templateUrl: './absence-management-dialog.component.html',
})
export class AbsenceManagementDialogComponent {
  public ButtonThemePalette = ButtonThemePalette;

  public AbsenceManagementDialogStep = AbsenceManagementDialogStep;
  public currentStep = AbsenceManagementDialogStep.TEASER;
  public steps = 2;

  public nextButtonLoading = false;
  private eventTracked = false;

  public defaultRosterAction = new FormControl(null, Validators.required);
  public rosterActionOptions: ChapterRadioButtonData[] = [
    {
      value: DefaultScheduledShiftAction.REMOVE_FROM_SCHEDULE,
      label: this.translate.instant('Remove from schedule'),
      description: this.translate.instant('Hide the shift only when the absence is approved.'),
    },
    {
      value: DefaultScheduledShiftAction.LEAVE_IN_SCHEDULE,
      label: this.translate.instant('Leave in schedule'),
      description: this.translate.instant('Keep the shift as scheduled, even during the absence.'),
    },
    {
      // Needs at least basic
      value: DefaultScheduledShiftAction.MOVE_TO_OPEN_SHIFT,
      label: this.translate.instant('Move to open shifts'),
      description: this.translate.instant('Make the shift available for others to pick up.'),
    },
  ];

  public constructor(
    @Inject(DIALOG_DATA) public readonly data: AbsenceManagementDialogData,
    private readonly trackingService: TrackingService,
    private readonly dialogRef: DialogRef<OnboardingDialogResult>,
    private readonly translate: TranslateService,
    private readonly service: AbsenceManagementOnboardingService,
    private readonly onboardingService: OnboardingService,
  ) {
    if (data.step) {
      this.currentStep = data.step;
    }
  }

  public get chapterCTAs() {
    return this.onboardingService.chapterCTAs;
  }

  public goBack() {
    this.currentStep = this.currentStep - 1;
  }

  public savePreferences() {
    this.nextButtonLoading = true;

    void this.service
      .savePreferences({
        defaultScheduledShiftAction: this.defaultRosterAction.value,
      })
      .pipe(
        switchMap(() => from(this.service.completeChapter(this.data.eventData))),
        finalize(() => (this.nextButtonLoading = false)),
      )
      .subscribe(() => {
        this.currentStep = AbsenceManagementDialogStep.SUMMARY;
      });
  }

  public close(result: OnboardingDialogResult) {
    if (this.currentStep === AbsenceManagementDialogStep.TEASER && !this.eventTracked) {
      this.trackingService.event('Onboarding Absence Management Started', this.data.eventData);
      this.eventTracked = true;
    }

    this.dialogRef.close(result);
  }
}
